// ============================================================
// AEO Grid Tab + shared Grid Editor
//
// Exports:
//   - AEOGridTab        : the per-client tab (list of grids + new button)
//   - AEOGridEditor     : the editor popup (shared by tab AND project step)
//   - AEO_REFERENCE_URL : the sample/example grid link
//
// AEO grids live on the client (aeo_grids table). The "Create AEO
// Grid" project step opens the same AEOGridEditor and saves into the
// same table, so grids created there also show up in this tab.
//
// Funnel stages are FIXED here (rows). Personas are flexible (columns).
// ============================================================
import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { format } from 'date-fns'
import { Plus, X, Trash2, ExternalLink, CheckCircle, Send, Download } from 'lucide-react'
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, PageOrientation, TableLayoutType } from 'docx'

// The example/template grid you shared (Lily Center sample)
export const AEO_REFERENCE_URL = 'https://docs.google.com/document/d/1Rm5zjp1C0h71kACrSSOeSmcgpaP17-bNxYDN-Nvf61M/edit'

// Fixed funnel stages (rows). Keyed internally by `key`.
const FUNNEL_STAGES = [
  { key: 'awareness',     label: 'Awareness' },
  { key: 'consideration', label: 'Consideration' },
  { key: 'evaluation',    label: 'Evaluation' },
  { key: 'decision',      label: 'Decision' },
]

const STATUS_META = {
  draft:              { label: 'Draft',              bg: 'var(--cream-dark)', color: 'var(--text-secondary)' },
  awaiting_approval:  { label: 'Awaiting approval',  bg: '#fff8e1',           color: '#8a6000' },
  approved:           { label: 'Approved',           bg: '#e8f5eb',           color: '#2e6e3a' },
}
const statusMeta = s => STATUS_META[s] || STATUS_META.draft

const safeFmt = (dateStr, fmt) => {
  if (!dateStr) return null
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return null
    return format(d, fmt)
  } catch { return null }
}

// Build a cell key from stage + persona index
const cellKey = (stageKey, personaIdx) => `${stageKey}|${personaIdx}`

// Export one grid as a Word .docx — a landscape table (rows = funnel stages,
// columns = personas, each cell = that persona's questions for that stage).
// Uses the same docx API + download pattern as the blog-brief export.
async function downloadGridDocx(grid, clientName) {
  const personas = (Array.isArray(grid?.personas) && grid.personas.length) ? grid.personas : ['Persona 1']
  const cells = (grid?.cells && typeof grid.cells === 'object') ? grid.cells : {}
  const NAVY = '1A2B4A'
  const margins = { top: 80, bottom: 80, left: 120, right: 120 }

  // Fixed column widths (twips) + FIXED layout so Word renders full-width
  // columns instead of auto-collapsing them. Landscape Letter usable width
  // is ~12960 twips (11in page, 1in margins each side).
  const USABLE = 12960
  const STAGE_W = 1700
  const personaW = Math.max(1400, Math.floor((USABLE - STAGE_W) / personas.length))
  const colWidths = [STAGE_W, ...personas.map(() => personaW)]
  const tableW = colWidths.reduce((a, b) => a + b, 0)
  const dxa = size => ({ size, type: WidthType.DXA })

  const headerCell = (text, w) => new TableCell({
    width: dxa(w), shading: { fill: NAVY }, margins,
    children: [new Paragraph({ children: [new TextRun({ text, bold: true, color: 'FFFFFF', size: 22 })] })],
  })
  const stageCell = label => new TableCell({
    width: dxa(STAGE_W), shading: { fill: 'F3EEF5' }, margins,
    children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, color: NAVY, size: 22 })] })],
  })
  const questionCell = raw => {
    const list = Array.isArray(raw) ? raw : (raw ? String(raw).split('\n') : [])
    const qs = list.map(q => (q || '').trim()).filter(Boolean)
    const paras = qs.length
      ? qs.map(q => new Paragraph({ bullet: { level: 0 }, spacing: { after: 40 }, children: [new TextRun({ text: q, size: 20 })] }))
      : [new Paragraph({ children: [new TextRun({ text: '\u2014', size: 20, color: '999999' })] })]
    return new TableCell({ width: dxa(personaW), margins, children: paras })
  }

  const headerRow = new TableRow({
    tableHeader: true,
    children: [headerCell('Funnel stage', STAGE_W), ...personas.map(p => headerCell(p || 'Persona', personaW))],
  })
  const bodyRows = FUNNEL_STAGES.map(stage => new TableRow({
    children: [stageCell(stage.label), ...personas.map((p, idx) => questionCell(cells[cellKey(stage.key, idx)]))],
  }))

  const table = new Table({
    layout: TableLayoutType.FIXED,
    columnWidths: colWidths,
    width: dxa(tableW),
    rows: [headerRow, ...bodyRows],
  })

  const doc = new Document({
    sections: [{
      properties: { page: { size: { orientation: PageOrientation.LANDSCAPE } } },
      children: [
        new Paragraph({ spacing: { after: 80 }, children: [new TextRun({ text: `AEO Content Grid \u2014 ${clientName || ''}`, bold: true, size: 36, color: NAVY })] }),
        new Paragraph({ spacing: { after: 240 }, children: [new TextRun({ text: `${grid?.name ? grid.name + ' \u00b7 ' : ''}${personas.length} persona${personas.length === 1 ? '' : 's'} \u00b7 Generated ${new Date().toLocaleDateString()}`, size: 22, color: '666666' })] }),
        table,
      ],
    }],
  })

  const blob = await Packer.toBlob(doc)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  const safe = s => (s || 'grid').replace(/[^a-z0-9]/gi, '_')
  a.href = url
  a.download = `AEO_grid_${safe(clientName)}_${safe(grid?.name)}.docx`
  a.click()
  URL.revokeObjectURL(url)
}

// ════════════════════════════════════════════════════════════
// TAB — list of a client's AEO grids
// ════════════════════════════════════════════════════════════
export function AEOGridTab({ client, profiles, currentUser }) {
  const toast = useToast()
  const [grids, setGrids] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingGrid, setEditingGrid] = useState(null)  // grid object or 'new'

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('aeo_grids')
      .select('*')
      .eq('client_id', client.id)
      .order('created_at', { ascending: false })
    if (error) { toast('Error loading grids: ' + error.message, 'error') }
    setGrids(data || [])
    setLoading(false)
  }, [client.id, toast])

  useEffect(() => { load() }, [load])

  const deleteGrid = async (grid) => {
    if (!window.confirm(`Delete "${grid.name}"? This cannot be undone.`)) return
    const { error } = await supabase.from('aeo_grids').delete().eq('id', grid.id)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Grid deleted', 'success')
    load()
  }

  return (
    <div>
      {/* Reference link */}
      <div style={{
        background: 'var(--cream)', border: '1px solid var(--border-light)', borderRadius: 10,
        padding: '12px 16px', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <span>An AEO content grid maps each audience persona's questions across the funnel — Awareness, Consideration, Evaluation, Decision.</span>
        <a href={AEO_REFERENCE_URL} target="_blank" rel="noreferrer"
          className="btn btn-ghost btn-sm" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          <ExternalLink size={13} /> See example grid
        </a>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="btn btn-gold" onClick={() => setEditingGrid('new')}>
          <Plus size={14} /> New AEO Grid
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
      ) : grids.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔠</div>
          <h3>No AEO grids yet</h3>
          <p>Build a content grid mapping this client's audience personas to the questions they ask at each funnel stage.</p>
          <button className="btn btn-gold" onClick={() => setEditingGrid('new')} style={{ marginTop: 12 }}>
            <Plus size={13} /> Create First Grid
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {grids.map(g => {
            const sm = statusMeta(g.status)
            const personaCount = Array.isArray(g.personas) ? g.personas.length : 0
            return (
              <div key={g.id} className="card" style={{ cursor: 'pointer', borderLeft: `4px solid ${g.status === 'approved' ? '#2e6e3a' : 'var(--gold)'}` }}
                onClick={() => setEditingGrid(g)}>
                <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{g.name}</div>
                    <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                      <span>{personaCount} {personaCount === 1 ? 'persona' : 'personas'}</span>
                      {g.created_at && <span>Created {safeFmt(g.created_at, 'MMM d, yyyy')}</span>}
                      {g.status === 'approved' && g.approved_at && <span>Approved {safeFmt(g.approved_at, 'MMM d')}</span>}
                    </div>
                  </div>
                  <span className="status-pill" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
                  <button className="btn-icon" title="Download as Word (.docx)"
                    onClick={e => { e.stopPropagation(); downloadGridDocx(g, client?.name) }}>
                    <Download size={14} />
                  </button>
                  <button className="btn-icon" style={{ color: 'var(--red)' }}
                    onClick={e => { e.stopPropagation(); deleteGrid(g) }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {editingGrid && (
        <AEOGridEditor
          client={client}
          profiles={profiles}
          currentUser={currentUser}
          grid={editingGrid === 'new' ? null : editingGrid}
          onClose={() => setEditingGrid(null)}
          onSaved={() => { setEditingGrid(null); load() }}
        />
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// EDITOR — shared popup used by the tab AND the project step
// ════════════════════════════════════════════════════════════
export function AEOGridEditor({ client, profiles, currentUser, grid, onClose, onSaved }) {
  const toast = useToast()
  const isEdit = !!grid

  const defaultName = `AEO Grid — ${format(new Date(), 'MMM yyyy')}`
  const [name, setName] = useState(grid?.name || defaultName)
  const [personas, setPersonas] = useState(
    Array.isArray(grid?.personas) && grid.personas.length > 0 ? grid.personas : ['Persona 1']
  )
  const [cells, setCells] = useState(grid?.cells && typeof grid.cells === 'object' ? grid.cells : {})
  const [status, setStatus] = useState(grid?.status || 'draft')
  const [saving, setSaving] = useState(false)

  // ─── Persona (column) operations ───
  const addPersona = () => setPersonas(ps => [...ps, `Persona ${ps.length + 1}`])
  const renamePersona = (idx, value) => setPersonas(ps => ps.map((p, i) => i === idx ? value : p))
  const removePersona = (idx) => {
    if (personas.length <= 1) { toast('Keep at least one persona', 'error'); return }
    if (!window.confirm(`Remove "${personas[idx]}" and all its questions?`)) return
    // Remove the column and re-key all cells whose persona index > idx
    setPersonas(ps => ps.filter((_, i) => i !== idx))
    setCells(prev => {
      const next = {}
      for (const [k, v] of Object.entries(prev)) {
        const [stageKey, pIdxStr] = k.split('|')
        const pIdx = parseInt(pIdxStr, 10)
        if (pIdx === idx) continue            // drop removed column
        const newIdx = pIdx > idx ? pIdx - 1 : pIdx
        next[cellKey(stageKey, newIdx)] = v
      }
      return next
    })
  }

  // ─── Cell operations ───
  // Each cell stores its questions as a single newline-separated string while editing.
  const getCellText = (stageKey, personaIdx) => {
    const v = cells[cellKey(stageKey, personaIdx)]
    if (Array.isArray(v)) return v.join('\n')
    return v || ''
  }
  const setCellText = (stageKey, personaIdx, text) => {
    setCells(prev => ({ ...prev, [cellKey(stageKey, personaIdx)]: text }))
  }

  // Normalize cells to arrays of trimmed questions for storage
  const normalizeCells = () => {
    const out = {}
    for (const [k, v] of Object.entries(cells)) {
      const arr = (Array.isArray(v) ? v.join('\n') : (v || ''))
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean)
      if (arr.length > 0) out[k] = arr
    }
    return out
  }

  const buildPayload = (overrides = {}) => ({
    client_id: client.id,
    name: name.trim() || defaultName,
    personas: personas.map(p => p.trim() || 'Untitled'),
    cells: normalizeCells(),
    ...overrides,
  })

  const save = async (extraOverrides = {}, successMsg = 'Grid saved') => {
    setSaving(true)
    try {
      let result
      if (isEdit) {
        result = await supabase.from('aeo_grids').update(buildPayload(extraOverrides)).eq('id', grid.id).select().single()
      } else {
        result = await supabase.from('aeo_grids').insert(buildPayload({ created_by: currentUser.id, ...extraOverrides })).select().single()
      }
      if (result.error) throw result.error
      toast(successMsg, 'success')
      onSaved && onSaved(result.data)
    } catch (e) {
      toast('Error: ' + e.message, 'error')
      setSaving(false)
    }
  }

  // Find the supervising AD (assigner) to notify. Lauren Parker is supervising_ad.
  const sendForApproval = async () => {
    setSaving(true)
    try {
      // Save first (insert or update), get the row back
      const payload = buildPayload({ status: 'awaiting_approval' })
      let result
      if (isEdit) {
        result = await supabase.from('aeo_grids').update(payload).eq('id', grid.id).select().single()
      } else {
        result = await supabase.from('aeo_grids').insert({ ...payload, created_by: currentUser.id }).select().single()
      }
      if (result.error) throw result.error

      // Notify the supervising AD(s)
      const { data: supers } = await supabase
        .from('profiles')
        .select('id')
        .eq('role', 'supervising_ad')
      const recipients = (supers || []).filter(s => s.id !== currentUser.id)
      if (recipients.length > 0) {
        await supabase.from('notifications').insert(
          recipients.map(r => ({
            user_id: r.id,
            type: 'comment',
            message: `AEO grid "${payload.name}" for ${client.name} is ready for your approval`,
          }))
        )
      }

      setStatus('awaiting_approval')
      toast('Sent for approval', 'success')
      onSaved && onSaved(result.data)
    } catch (e) {
      toast('Error: ' + e.message, 'error')
      setSaving(false)
    }
  }

  const markApproved = async () => {
    await save({ status: 'approved', approval_by: currentUser.id, approved_at: new Date().toISOString() }, 'Grid approved')
  }
  const reopen = async () => {
    await save({ status: 'draft', approval_by: null, approved_at: null }, 'Grid reopened to draft')
  }

  const sm = statusMeta(status)
  // Only supervising ADs can approve
  const canApprove = profiles.find(p => p.id === currentUser.id)?.role === 'supervising_ad'

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 95, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 20px', overflowY: 'auto' }}>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(26,43,74,0.35)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: '100%', maxWidth: 1100, background: '#fff', borderRadius: 12, boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 80px)' }}>
        {/* Header */}
        <div style={{ background: 'var(--navy)', padding: '16px 22px', color: '#fff', borderTopLeftRadius: 12, borderTopRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              AEO Content Grid · {client.name}
            </div>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              style={{ background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.25)', color: '#fff', fontFamily: 'var(--font-display)', fontSize: 18, marginTop: 4, width: '100%', padding: '2px 0', outline: 'none' }}
            />
          </div>
          <span className="status-pill" style={{ background: sm.bg, color: sm.color, flexShrink: 0 }}>{sm.label}</span>
          <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', flexShrink: 0 }} onClick={onClose}><X size={16} /></button>
        </div>

        {/* Grid body (scrolls) */}
        <div style={{ padding: '18px 22px', flex: 1, overflow: 'auto' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            Type one question per line in each cell. Add a persona column with the + button on the right.
            <a href={AEO_REFERENCE_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', marginLeft: 6 }}>See example ↗</a>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 200 + personas.length * 240 }}>
              <thead>
                <tr>
                  <th style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--cream)', border: '1px solid var(--border-light)', padding: '8px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary)', width: 130, minWidth: 130 }}>
                    Funnel Stage
                  </th>
                  {personas.map((p, idx) => (
                    <th key={idx} style={{ border: '1px solid var(--border-light)', padding: '6px 8px', background: 'var(--cream)', minWidth: 240, verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                        <textarea
                          value={p}
                          onChange={e => renamePersona(idx, e.target.value)}
                          placeholder={`Persona ${idx + 1}`}
                          rows={1}
                          ref={el => {
                            if (el) {
                              el.style.height = 'auto'
                              el.style.height = el.scrollHeight + 'px'
                            }
                          }}
                          style={{ flex: 1, fontSize: 12.5, fontWeight: 600, border: '1px solid transparent', borderRadius: 4, padding: '4px 6px', background: '#fff', minWidth: 0, resize: 'none', overflow: 'hidden', fontFamily: 'var(--font-body)', lineHeight: 1.4, outline: 'none' }}
                          onFocus={e => e.target.style.border = '1px solid var(--border)'}
                          onBlur={e => e.target.style.border = '1px solid transparent'}
                        />
                        <button className="btn-icon" title="Remove persona" onClick={() => removePersona(idx)} style={{ flexShrink: 0, marginTop: 2 }}>
                          <X size={12} />
                        </button>
                      </div>
                    </th>
                  ))}
                  <th style={{ border: 'none', padding: '6px', width: 44, verticalAlign: 'top' }}>
                    <button className="btn-icon" title="Add persona column" onClick={addPersona}
                      style={{ background: 'var(--navy)', color: '#fff', border: 'none' }}>
                      <Plus size={14} />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {FUNNEL_STAGES.map(stage => (
                  <tr key={stage.key}>
                    <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--cream)', border: '1px solid var(--border-light)', padding: '8px 10px', fontSize: 12, fontWeight: 700, color: 'var(--navy)', verticalAlign: 'top', width: 130, minWidth: 130 }}>
                      {stage.label}
                    </td>
                    {personas.map((_, pIdx) => (
                      <td key={pIdx} style={{ border: '1px solid var(--border-light)', padding: 0, verticalAlign: 'top' }}>
                        <textarea
                          value={getCellText(stage.key, pIdx)}
                          onChange={e => setCellText(stage.key, pIdx, e.target.value)}
                          placeholder="One question per line…"
                          rows={4}
                          style={{ width: '100%', border: 'none', resize: 'vertical', padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5, fontFamily: 'var(--font-body)', outline: 'none', background: 'transparent', minHeight: 80 }}
                        />
                      </td>
                    ))}
                    <td style={{ border: 'none' }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ padding: 16, borderTop: '1px solid var(--border-light)', display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <div style={{ flex: 1 }} />

          {status === 'draft' && (
            <>
              <button className="btn btn-ghost" onClick={() => save()} disabled={saving}>{saving ? 'Saving…' : '💾 Save draft'}</button>
              <button className="btn btn-gold" onClick={sendForApproval} disabled={saving}>
                <Send size={13} /> Send to Lauren for approval
              </button>
            </>
          )}

          {status === 'awaiting_approval' && (
            <>
              <button className="btn btn-ghost" onClick={() => save()} disabled={saving}>{saving ? 'Saving…' : '💾 Save changes'}</button>
              {canApprove ? (
                <>
                  <button className="btn btn-ghost" onClick={reopen} disabled={saving}>↺ Back to draft</button>
                  <button className="btn btn-gold" onClick={markApproved} disabled={saving}>
                    <CheckCircle size={13} /> Approve grid
                  </button>
                </>
              ) : (
                <span style={{ fontSize: 12, color: '#8a6000', fontWeight: 600 }}>⏳ Waiting on Lauren's approval</span>
              )}
            </>
          )}

          {status === 'approved' && (
            <>
              <button className="btn btn-ghost" onClick={() => save()} disabled={saving}>{saving ? 'Saving…' : '💾 Save changes'}</button>
              <button className="btn btn-ghost" onClick={reopen} disabled={saving}>↺ Reopen to draft</button>
              <span style={{ fontSize: 12, color: '#2e6e3a', fontWeight: 600 }}>✓ Approved</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}